import type { ReactNode } from "react"

/**
 * Pinta el documento del editor **con componentes de React**, no con HTML.
 *
 * ⚠ **Ésta es la pieza que hace posible la regla 12.** El editor guarda su documento
 * en JSON (`getJSON()`), y esto lo recorre nodo a nodo. La alternativa —guardar el
 * HTML que produce el editor y volcarlo con `dangerouslySetInnerHTML`— está prohibida
 * y Biome la marca como error: el cuerpo de un procedimiento es contenido que
 * escriben personas, y el día que alguien pegue algo copiado de fuera, ese algo se
 * ejecuta en nuestro origen con la sesión de quien lo lee.
 *
 * ⚠ **Un tipo de nodo desconocido no rompe el documento: se ignora.** Si mañana el
 * editor gana una extensión y alguien guarda un nodo que esta versión no conoce,
 * reventar aquí dejaría el documento entero en blanco. Se pierde ese bloque y se
 * pinta el resto — que es lo que un lector puede usar.
 *
 * ⚠ **Los encabezados NO se numeran aquí.** La numeración de secciones la pone el CSS
 * con contadores, para que al imprimir siga siendo texto y no números incrustados que
 * se descoloquen al insertar una sección en medio.
 */

/** Un nodo del documento, tal como lo guarda el editor. */
export type NodoDoc = {
  type?: string
  text?: string
  attrs?: Record<string, unknown>
  marks?: { type: string }[]
  content?: NodoDoc[]
}

/** Las marcas de texto que se conocen. Una desconocida deja el texto sin adornar. */
function conMarcas(texto: string, marcas: { type: string }[] | undefined): ReactNode {
  let salida: ReactNode = texto
  for (const marca of marcas ?? []) {
    if (marca.type === "bold") salida = <strong>{salida}</strong>
    else if (marca.type === "italic") salida = <em>{salida}</em>
    else if (marca.type === "code") salida = <code>{salida}</code>
    else if (marca.type === "strike") salida = <s>{salida}</s>
  }
  return salida
}

function alineacion(nodo: NodoDoc): string | undefined {
  const valor = nodo.attrs?.textAlign
  return typeof valor === "string" && valor !== "left" ? `text-${valor}` : undefined
}

function hijos(nodo: NodoDoc): ReactNode {
  // ⚠ La clave es la POSICIÓN, y aquí es lo correcto: un nodo del documento no tiene
  // identidad propia —no hay id que guardar— y su sitio en el árbol es lo único que
  // lo distingue. El documento no se reordena parcialmente: se repinta entero al
  // guardar. Se compone con el tipo para que el reconciliador no reuse un párrafo
  // como celda de tabla al cambiar la estructura.
  return (nodo.content ?? []).map((hijo, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: un nodo del documento no tiene id; su posicion ES su identidad y el arbol se repinta entero
    <Nodo key={`${i}-${hijo.type ?? "?"}`} nodo={hijo} />
  ))
}

function Nodo({ nodo }: { nodo: NodoDoc }) {
  switch (nodo.type) {
    case "text":
      return <>{conMarcas(nodo.text ?? "", nodo.marks)}</>
    case "paragraph":
      return <p className={alineacion(nodo)}>{hijos(nodo)}</p>
    case "heading": {
      const nivel = Number(nodo.attrs?.level ?? 2)
      // Sólo H2 y H3: el editor no ofrece más, y el H1 es el título del documento.
      return nivel <= 2 ? (
        <h2 className={alineacion(nodo)}>{hijos(nodo)}</h2>
      ) : (
        <h3 className={alineacion(nodo)}>{hijos(nodo)}</h3>
      )
    }
    case "bulletList":
      return <ul>{hijos(nodo)}</ul>
    case "orderedList":
      return <ol>{hijos(nodo)}</ol>
    case "listItem":
      return <li>{hijos(nodo)}</li>
    case "blockquote":
      return <blockquote>{hijos(nodo)}</blockquote>
    case "codeBlock":
      return (
        <pre>
          <code>{hijos(nodo)}</code>
        </pre>
      )
    case "horizontalRule":
      return <hr />
    case "hardBreak":
      return <br />
    case "table":
      // El contenedor con scroll propio: una tabla ancha no puede hacer que la
      // página entera se desplace en horizontal.
      return (
        <div className="doc-tabla">
          <table>
            <tbody>{hijos(nodo)}</tbody>
          </table>
        </div>
      )
    case "tableRow":
      return <tr>{hijos(nodo)}</tr>
    case "tableHeader":
      return (
        <th colSpan={Number(nodo.attrs?.colspan ?? 1)} rowSpan={Number(nodo.attrs?.rowspan ?? 1)}>
          {hijos(nodo)}
        </th>
      )
    case "tableCell":
      return (
        <td colSpan={Number(nodo.attrs?.colspan ?? 1)} rowSpan={Number(nodo.attrs?.rowspan ?? 1)}>
          {hijos(nodo)}
        </td>
      )
    case "saltoDePagina":
      // Sólo tiene efecto al imprimir. En pantalla es una línea de puntos.
      return <div className="doc-salto" aria-hidden="true" />
    case "doc":
      return <>{hijos(nodo)}</>
    default:
      // Desconocido: se ignora. Ver la cabecera del módulo.
      return null
  }
}

export function Documento({ cuerpo }: { cuerpo: NodoDoc | null | undefined }) {
  if (!cuerpo?.content?.length) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Este documento todavía no tiene contenido.
      </p>
    )
  }
  return (
    <div className="doc-prosa">
      <Nodo nodo={cuerpo} />
    </div>
  )
}
